import React, { useRef, useState } from 'react';
import _ from 'lodash';
import styles from 'components/transactionsShuttle/TransactionsShuttle.module.css';
import FullPageLoader from 'components/loaders/FullPageLoader';
import BlazeTable from 'components/blazeTable/BlazeTable';
import TagTextFilter from 'components/ui/filters/TagTextFilter';
import { MESSAGING, MockService, PriorityMode } from 'types/rest/mockServiceTypes';
import TransactionsDropzone from 'components/dropzone/TransactionsDropzone';
import { MockServiceTemplate } from 'store/mockServiceTemplates/mockServiceTemplateTypes';
import SubTitle from 'components/dropzone/subtitle/SubTitle';
import { BlazeTableColDef, BlazeTableOptions, SelectAllType } from 'components/blazeTable/BlazeTable.types';
import SimpleTagList from 'components/ui/tags/SimpleTagList';
import { useTranslation } from 'react-i18next';
import { PanePosition, ShuttlePaneControls } from 'store/transactionShuttle/TransactionShuttleTypes';
import TransactionSimpleDetail from 'components/transactionsShuttle/transactionsSimpleTable/TransactionSimpleDetail';
import { Transaction, TransactionType } from 'types/rest/assetCatalogTypes';
import EditTransactionModal from '../modal/EditTransactionModal';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { EmptyTransaction } from 'views/AssetCatalog/Transactions/TransactionEditor/TransactionEditor';
import i18n from 'i18n';
import { fetchTransactionsForTemplate } from 'store/mockServiceTemplates/mockServiceTemplateDetail/mockServiceTemplateDetailActions';
import { fetchTransactionsForShuttlePane } from 'store/mockServices/mockServiceDetail/mockServiceDetailActions';
import { useDispatch } from 'react-redux';
import BzmIcon from '../icons/BzmIcon';
import { isMockServiceRunning } from 'utils/mockServiceUtils';
import { AssignmentActionType } from './TransactionShuttle.types';
import { SortConfiguration } from 'types/rest/paginationTypes';
import PrioritySwitcher from './PrioritySwitcher';
import EditMessagingModal from '../modal/EditMessagingModal';
import VirtualServiceTypeIcon from '../icons/VirtualServiceTypeIcon';
import { isTemplate } from '../../utils/miscUtil';
import ExpandLink from '../blazeTable/ExpandLink';

type ShuttlePaneStatelessProps = {
  title: any;
  context: MockService | MockServiceTemplate;
  isDropzoneVisible: boolean;
  disabled: boolean;
  paneState: ShuttlePaneControls;
  transactions: Transaction[];
  showTxnPriority: boolean;
  updateActivePage: (activePage: number) => void;
  selectTransactions: (transactionIds: number[], selectAll?: SelectAllType) => void;
  expandItem: (item: number | null) => void;
  filterChanged: (search: string) => void;
  reloadHandler: () => void;
  isSearchDisabled: boolean;
  tableClassName?: string;
  updateTransaction?: (transaction: Transaction) => void;
  noResultPlaceholder?: any;
  emptyPlaceholder?: any;
  paneType: PanePosition;
  moveTransactions: (assignmentType: AssignmentActionType, txId?: number) => void;
  sort: SortConfiguration;
  onSort: (sort: SortConfiguration) => void;
  onPriorityModeChanged?: (mode: PriorityMode) => void;
  priorityMode?: PriorityMode;
  onUpdateShowPriorityModal?: (isShown: boolean) => void;
  transactionType: TransactionType;
};

const TransactionShuttlePane = (props: ShuttlePaneStatelessProps) => {
  const { t } = useTranslation('mockServices');
  const [editedTransaction, setEditedTransaction] = useState<any>(null);
  const [isNew, setIsNew] = useState<any>(false);
  const dispatch = useDispatch();
  const tableRef = useRef<BlazeTable>(null);

  const setParamsEditTransaction = (isNew: boolean, transaction?: Transaction) => {
    setEditedTransaction(
      transaction ? transaction : new EmptyTransaction(props.transactionType, props.context.serviceId),
    );
    setIsNew(isNew);
  };

  const createAddNewTransactionDom = () => {
    return (
      <Col>
        <div className={'float-right mr-4 mt-3'}>
          <button
            disabled={props.disabled}
            title={t('transactions:txnTable.addNewTransaction')}
            onClick={() => setParamsEditTransaction(true)}
            id="createServiceMockButton"
            className={`btn btn-primary btn-circular btn-sm`}
          >
            <BzmIcon variant={'icon-bzm-add'} />
          </button>
        </div>
      </Col>
    );
  };

  const getTransactionActions = (transaction: Transaction) => {
    return (
      <>
        {/* Edit Button */}
        <div className="flex-table-actions">
          <button
            disabled={props.disabled}
            className={'btn btn-link-dark btn-no-hover'}
            style={{ zIndex: 100 }}
            onClick={() => setParamsEditTransaction(false, transaction)}
            title={'Edit Transaction'}
          >
            <BzmIcon variant={'icon-bzm-edit'} className={'icon-xs'} />
          </button>
          {getMoveButton(transaction)}
        </div>
        {/* ./Edit Button */}
      </>
    );
  };

  const moveTransaction = (transaction: Transaction) => {
    props.moveTransactions(
      props.paneType === 'LEFT' ? AssignmentActionType.ADD : AssignmentActionType.REMOVE,
      transaction.id as number,
    );
  };

  const getMoveButton = (transaction: Transaction) => {
    const side = props.paneType === 'LEFT' ? 'right' : 'left';
    const title =
      props.paneType === 'LEFT' ? i18n.t('transactions:actions.includeTx') : i18n.t('transactions:actions.removeTx');
    const moveIcon = 'icon-bzm-single-assign-' + side + ' icon-xs';
    return (
      <button
        className={'btn btn-link-dark btn-no-hover'}
        style={{ zIndex: 100 }}
        onClick={() => moveTransaction(transaction)}
        title={title}
        disabled={props.disabled}
      >
        <span className={moveIcon}></span>
      </button>
    );
  };

  const generateDropzoneDom = () => {
    let dropZone = <></>;

    if (props.isDropzoneVisible) {
      const contextName = (props.context as MockService).type ? 'MOCK_SERVICE' : 'MOCK_SERVICE_TEMPLATE';
      dropZone = (
        <div className={styles.dropzone}>
          <TransactionsDropzone
            preselectedService={props.context.serviceId}
            context={contextName}
            disabled={props.disabled}
            subtitle={<SubTitle />}
            processingFinishedHandler={() => props.reloadHandler()}
            acceptFiles={['.har', '.json', '.yaml', '.yml', '.zip', '.wsdl']}
          />
        </div>
      );
    }

    return dropZone;
  };

  const columnDef: BlazeTableColDef[] = [
    {
      header: t('transactions:txnTable.id'),
      accessor: 'id',
      width: 70,
    },
    {
      header: t('transactions:txnTable.name'),
      accessor: 'name',
      isExpandLink: true,
      cellComponent: txn => {
        return (
          <div className="d-flex align-items-center">
            <div>
              <VirtualServiceTypeIcon type={txn.type} className={'mr-2 align-middle'} />
            </div>
            <ExpandLink
              id={txn.id ? txn.id : ''}
              value={txn.name}
              onToggleExpandDetail={() => tableRef.current.expandRowHandler(txn.id)}
            />
          </div>
        );
      },
    },
    {
      header: t('transactions:txnTable.tags'),
      cellComponent: (t: Transaction) => <SimpleTagList tags={t.tags} />,
      accessor: 'tags',
      isSortable: false,
      width: 190,
    },
    {
      header: 'Actions',
      cellComponent: getTransactionActions,
      accessor: 'none1',
      width: 80,
      isSortable: false,
    },
  ];

  //Only for right pane
  if (!props.isDropzoneVisible) {
    columnDef.splice(3, 0, {
      header: t('transactions:txnTable.priority'),
      accessor: 'priority',
      width: 80,
    });
  }

  const updateTransactionFromModal = (transaction: Transaction) => {
    //Refresh panes if it is brand-new transaction creation
    if (isNew) {
      dispatch(
        isTemplate(props.context)
          ? fetchTransactionsForTemplate(props.context as MockServiceTemplate, 'LEFT', props.transactionType)
          : fetchTransactionsForShuttlePane(props.context as MockService, 'LEFT', props.transactionType),
      );
    } else {
      // Expand item if it is open, this is done because of WebHooks
      // in the simple detail which need to be refreshed if edited
      if (props.paneState.expanded === transaction.id) {
        props.expandItem(null);
      }
    }

    if (props.updateTransaction) {
      props.updateTransaction(transaction);
    }
  };

  const paneTransactionsTableOptions: BlazeTableOptions = {
    showRowExpander: true,
    columnDef: columnDef,
    expandDetailComponent: (transaction: Transaction) => (
      <TransactionSimpleDetail
        transaction={transaction}
        context={props.context}
        disabled={props.disabled}
        showTxnPriority={props.showTxnPriority}
        onUpdateTransaction={props.updateTransaction}
        showTransactionLink={
          props.paneType === 'RIGHT' && isMockServiceRunning(props.context) && props.context.isDraft === false
        }
        paneType={props.paneType}
        transactionType={props.transactionType}
      />
    ),
    selectable: true,
    sortable: true,
    pageable: true,
    pageSize: 8,
    customTableClassName: props.tableClassName,
    disabled: props.disabled,
    paginationOverride: {
      activePage: props.paneState.activePage,
      pageSize: props.paneState.pageSize,
      total: props.paneState.totalResults,
    },
    sortingOverride: props.sort,
    noResultsText: props.noResultPlaceholder,
    searchTerm: props.paneState.searchTerm,
    defaultEmpty: props.emptyPlaceholder,
    selectAllDropdown: true,
    selectAllType: props.paneState.selectAll,
  };

  return (
    <>
      {!_.isNil(editedTransaction) &&
        (editedTransaction.type === 'HTTP' ? (
          <EditTransactionModal
            onClose={() => {
              setEditedTransaction(null);
            }}
            isReadOnly={false}
            transaction={editedTransaction}
            onUpdate={updateTransactionFromModal}
            isNew={isNew}
          />
        ) : (
          <EditMessagingModal
            onClose={() => {
              setEditedTransaction(null);
            }}
            isReadOnly={false}
            transaction={editedTransaction}
            onUpdate={updateTransactionFromModal}
            isNew={isNew}
          />
        ))}

      <div>
        {/* Advanced priority setting is for RIGHT pane only */}
        {props.isDropzoneVisible === false && (
          <div className={'float-right mt-1'}>
            <PrioritySwitcher
              disabled={props.disabled}
              onPriorityModeChanged={props.onPriorityModeChanged}
              priorityMode={props.priorityMode}
              context={props.context}
              onUpdatePriorityModal={props.onUpdateShowPriorityModal}
            />
          </div>
        )}
        <h2 className={styles.titleText}>
          {props.title} ({props.paneState.totalResults})
        </h2>
        <div className={`${styles.serviceTransactionBackground}`}>
          {/* Dropzone */}
          {generateDropzoneDom()}
          {/* ./ Dropzone */}

          <Row>
            <Col>
              {!props.isSearchDisabled && (
                <div className={styles.transactionsFilter}>
                  <TagTextFilter
                    inline
                    className="mb-3"
                    defaultValue={props.paneState.searchTerm}
                    setSearchTerm={props.filterChanged}
                    context={'transactions'}
                  />
                  <br />
                </div>
              )}
            </Col>
            {props.paneType === 'LEFT' && createAddNewTransactionDom()}
          </Row>

          {/* Pane Table */}
          <div
            className={styles.availableServiceTransactions}
            style={{ height: props.isDropzoneVisible ? '536px' : '640px' }}
          >
            <div className={styles.availableServiceTransactionsTable} style={{ position: 'relative' }}>
              <div style={{ position: 'relative' }}>
                {props.paneState.isLoading && (
                  <div className="blur-background">
                    <FullPageLoader loadingText={i18n.t('transactions:actions.loadingTxns')} />
                  </div>
                )}
                <div className={props.paneState.isLoading ? 'blur' : ''}>
                  <BlazeTable
                    expandedItem={props.paneState.expanded}
                    selectedItems={props.paneState.selected}
                    data={props.transactions}
                    options={paneTransactionsTableOptions}
                    onSort={props.onSort}
                    onPageChange={props.updateActivePage}
                    onSelectionChange={props.selectTransactions}
                    onExpandedItemChange={props.expandItem}
                    ref={tableRef}
                  />
                </div>
              </div>
            </div>
          </div>
          {/* ./Pane Table */}
        </div>
      </div>
    </>
  );
};

export default TransactionShuttlePane;
